import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

interface Props { before: string; after: string; alt: string; }

export function BeforeAfterSlider({ before, after, alt }: Props) {
  const [pos, setPos] = useState(50);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[4/3] overflow-hidden select-none cursor-ew-resize bg-ink"
      onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
      onMouseMove={(e) => { if (dragging.current) setFromClientX(e.clientX); }}
      onMouseUp={() => { dragging.current = false; }}
      onMouseLeave={() => { dragging.current = false; }}
      onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
      onTouchMove={(e) => { if (dragging.current) setFromClientX(e.touches[0].clientX); }}
      onTouchEnd={() => { dragging.current = false; }}
    >
      <img src={after} alt={`${alt} — after`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" fetchPriority="low" decoding="async" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt={`${alt} — before`} className="absolute inset-0 h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} loading="lazy" fetchPriority="low" decoding="async" />
      </div>
      <span className="absolute top-4 left-4 bg-ink/80 text-cream text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">Before</span>
      <span className="absolute top-4 right-4 bg-accent text-accent-foreground text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">After</span>
      <div className="absolute top-0 bottom-0 w-px bg-cream pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-cream shadow-lg flex items-center justify-center">
          <MoveHorizontal className="h-5 w-5 text-ink" />
        </div>
      </div>
    </div>
  );
}
